/* eslint-disable no-nested-ternary */
import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from 'reactstrap';
import {
  Button,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tag,
} from '@jem-open/jem-ui';
import moment from 'moment';
import Swal from 'sweetalert2';
import { environment } from '../../../environment/environment';
import { DataTable, sortableHeader } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';
import { Context } from '../../../state/GlobalStore';
import RiskyAdvancesTable from './RiskyAdvancesTable';

import apiResponseHandler from '../../../utils/apiResponseHandler';
import CustomToolTip from '../../../components/Tooltip/CustomToolTip';
import copyTargetText from '../../../utils/clipboard';
import removeLeadingTrailingChar from '../../../utils/removeLeadingTrailingChar';
import CapitalizeFirstLetter from '../../../utils/CapitalizeFirstLetter';

let cancelToken;

const STATUS_TAG_VARIANT = {
  paid: 'success',
  pending: 'pending',
  rejected: 'failed',
  accepted: 'processing',
};

// Resolve a row's sort value via accessor, normalising empties to null.
const getSortValue = (accessor) => {
  const get =
    typeof accessor === 'function' ? accessor : (item) => item[accessor];
  return (item) => {
    const value = get(item);
    return value === null || value === undefined || value === '' ? null : value;
  };
};

// Compare two already-resolved values: empties last, numbers numerically,
// everything else as case-insensitive strings.
const compareValues = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const aNum = typeof a === 'number' ? a : Number(a);
  const bNum = typeof b === 'number' ? b : Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return aNum - bNum;
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
};

const compareByAsc = (accessor) => {
  const getValue = getSortValue(accessor);
  return (a, b) => compareValues(getValue(a), getValue(b));
};

const compareByDesc = (accessor) => {
  const getValue = getSortValue(accessor);
  return (a, b) => {
    const va = getValue(a);
    const vb = getValue(b);
    // Keep empties at the bottom regardless of sort direction.
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return -compareValues(va, vb);
  };
};

class AdvanceList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      advance_requests: [],
      query: '',
      status: 'pending',
      risky: false,
      loading: false,
      page: 1,
    };
  }

  static contextType = Context;

  componentDidMount() {
    this.getAdvanceRequests();
  }

  getAdvanceRequests(params) {
    this.setState({ loading: true });
    const { status, page, query } = this.state;
    const headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel('Operation canceled due to new request.');
    }
    // Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();

    let paramsValue = {
      status: status === 'all' ? null : status,
      page,
      pagination: true,
      search: query || null,
      page_size: 40,
      ...params,
    };

    // dont show risky advances for pending advances
    if (status === 'pending') {
      paramsValue = {
        risky: false,
        ...paramsValue,
      };
    }

    axios
      .get(`${environment.baseUrl}/advancerequests/`, {
        headers,
        params: paramsValue,
        cancelToken: cancelToken.token,
      })
      .then((res) => {
        this.setState({
          advance_requests: res.data.results,
          pages: res.data.pages,
          loading: false,
        });
      })
      .catch((error) => {
        if (error.message !== 'Operation canceled due to new request.') {
          apiResponseHandler.handleApiError('Error loading advances.', error);
        }
        this.setState({ loading: false });
      });
  }

  onChange = (e) => {
    const { value } = e.target;
    this.setState(
      {
        query: value,
        page: 1,
      },
      () => this.getAdvanceRequests(),
    );
  };

  sortBy(accessor) {
    const arrayCopy = [...this.state.advance_requests];
    const arrInStr = JSON.stringify(arrayCopy);
    arrayCopy.sort(compareByAsc(accessor));
    const arrInStr1 = JSON.stringify(arrayCopy);
    if (arrInStr === arrInStr1) {
      arrayCopy.sort(compareByDesc(accessor));
    }
    this.setState({ advance_requests: arrayCopy });
  }

  handleSort = (accessor) => this.sortBy(accessor);

  changeAdvanceStatus = (id, status) => {
    const request = axios
      .patch(`${environment.baseUrl}/advancerequests/${id}/`, {
        status,
      })
      .then((_res) => {
        Swal.fire({
          title: `Advance marked ${status}`,
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        this.getAdvanceRequests();
        const { fetchStats } = this.context;
        fetchStats();
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
      });
    return request;
  };

  markAllPendingPaid = () => {
    const promises = this.state.advance_requests
      .filter((advance) => advance.status === 'pending')
      .map((advance) =>
        axios.patch(`${environment.baseUrl}/advancerequests/${advance.id}/`, {
          status: 'paid',
        }),
      );
    Promise.all(promises)
      .then((_res) => {
        Swal.fire({
          title: 'All pending advances marked paid',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
      })
      .finally(() => {
        this.getAdvanceRequests();
        const { fetchStats } = this.context;
        fetchStats();
      });
  };

  setPage = (page) => {
    this.setState({ page }, () => this.getAdvanceRequests());
  };

  deleteAdvance(id) {
    Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to delete this advance?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${environment.baseUrl}/advancerequests/${id}/`)
          .then((_res) => {
            Swal.fire({
              title: 'Advance was deleted',
              icon: 'success',
            });
            this.getAdvanceRequests();
            const { fetchStats } = this.context;
            fetchStats();
          })
          .catch((error) => {
            apiResponseHandler.handleApiError('Error', error);
          });
      }
    });
  }

  /**
   * Show all risky advances that are still pending
   *
   */
  showRiskyAdvances() {
    this.setState({ showRiskyAdvances: true });
  }

  /**
   * Show all pending, non-risky advances
   *
   */
  clearRiskyFilters() {
    this.setState({ showRiskyAdvances: false });
    this.getAdvanceRequests();
  }

  render() {
    const {
      showRiskyAdvances,
      advance_requests,
      loading,
      status,
      page,
      pages,
    } = this.state;

    const columns = [
      {
        key: 'uuid',
        label: sortableHeader('UUID', 'uuid', this.handleSort),
        width: '110px',
        render: (value) => (
          <div className="max-w-[100px] truncate" title={value}>
            {value}
          </div>
        ),
      },
      {
        key: 'source_id',
        label: sortableHeader(
          <>
            Stitch ID{' '}
            <i
              id="tooltip-id"
              className="fa fa-question-circle"
              aria-hidden="true"
            />
          </>,
          'source_id',
          this.handleSort
        ),
        width: '220px',
        render: (value) => (
          <button
            type="button"
            onClick={copyTargetText}
            className="block max-w-[200px] cursor-pointer truncate border-0 bg-transparent p-0 text-left text-inherit [font:inherit]"
            title={value || '-'}>
            {value || '-'}
          </button>
        ),
      },
      {
        key: 'employee',
        label: sortableHeader(
          'Employee',
          (row) => `${row.employee.first_name} ${row.employee.last_name}`,
          this.handleSort
        ),
        render: (value, row) => (
          <Link to={`/admin/employees/${row.employee.id}`}>
            {`${row.employee.first_name} ${row.employee.last_name}`}
          </Link>
        ),
      },
      {
        key: 'employer',
        label: sortableHeader(
          'Employer',
          (row) =>
            row.employee.employer ? row.employee.employer.company_name : '',
          this.handleSort
        ),
        render: (value, row) => row.employee.employer?.company_name,
      },
      {
        key: 'amount_requested',
        label: sortableHeader('Amount', 'amount_requested', this.handleSort),
        render: (value) => `R ${value}`,
      },
      {
        key: 'instant_payment',
        label: sortableHeader(
          'Instant Payment',
          'instant_payment',
          this.handleSort
        ),
        render: (value) => (value ? 'Yes' : 'No'),
      },
      {
        key: 'date_created',
        label: sortableHeader(
          'Date Requested',
          'date_created',
          this.handleSort
        ),
        width: '180px',
        render: (value) => moment(value).format('DD MMM YY, h:mm a'),
      },
      {
        key: 'status',
        label: sortableHeader('Status', 'status', this.handleSort),
        render: (value) => (
          <Tag variant={STATUS_TAG_VARIANT[value] || 'neutral'}>
            {CapitalizeFirstLetter(value)}
          </Tag>
        ),
      },
      {
        key: 'risky_reasons',
        label: <span className="normal-case">Risky Reason</span>,
        render: (value, row) =>
          row.is_risky ? (
            <ul className="m-none pl-md">
              {removeLeadingTrailingChar(row.risky_reasons, ',')
                .split(',')
                .map((reason) => (
                  <li key={reason}>{CapitalizeFirstLetter(reason)}</li>
                ))}
            </ul>
          ) : (
            '-'
          ),
      },
      {
        key: 'status_reason',
        label: sortableHeader(
          <>
            Status Reason{' '}
            <i
              id="tooltip-status"
              className="fa fa-question-circle"
              aria-hidden="true"
            />
          </>,
          'status_reason',
          this.handleSort
        ),
        render: (value) => value || '-',
      },
      {
        key: 'actions',
        label: <span className="normal-case">Actions</span>,
        width: '230px',
        render: (value, row) => (
          <div className="flex items-center justify-end gap-xs">
            <Button
              size="sm"
              variant="outline"
              onClick={() => this.props.history.push(`/admin/advances/${row.id}`)}>
              View
            </Button>
            {row.status === 'pending' && (
              <>
                <Button
                  size="sm"
                  variant="approve"
                  onClick={() => this.changeAdvanceStatus(row.id, 'paid')}>
                  Paid
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => this.changeAdvanceStatus(row.id, 'rejected')}>
                  Reject
                </Button>
              </>
            )}
          </div>
        ),
      },
    ];

    const emptyState = (
      <EmptyState icon="fas fa-hand-holding-usd" message="No advances found" />
    );

    return (
      <div className="min-h-screen bg-white p-md">
        <div
          className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
          style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
          <div>
            <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
              Advances
            </h1>
            <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
              Review and process employee advance requests.
            </p>
          </div>
        </div>

        <CustomToolTip id="tooltip-id">
          <p>
            For transactions made using{' '}
            <span style={{ fontWeight: 'bold' }}>INSTANT PAYMENT</span>
          </p>
        </CustomToolTip>
        <CustomToolTip id="tooltip-status">
          <p>
            For transactions in the{' '}
            <span style={{ fontWeight: 'bold' }}>FAILED</span> or{' '}
            <span style={{ fontWeight: 'bold' }}>REJECTED</span> state
          </p>
        </CustomToolTip>

        <div className="mb-md flex flex-wrap items-center gap-sm">
          <div className="min-w-[280px] max-w-md flex-1">
            <SearchInput
              value={this.state.query}
              placeholder="Search Advances"
              onChange={this.onChange}
              onClear={() =>
                this.setState({ query: '', page: 1 }, () =>
                  this.getAdvanceRequests(),
                )
              }
              disabled={showRiskyAdvances}
            />
          </div>
          <div className="w-56">
            <Select
              value={status}
              onValueChange={(value) =>
                this.setState(
                  { status: value, page: 1 },
                  this.getAdvanceRequests,
                )
              }
              disabled={showRiskyAdvances}>
              <SelectTrigger>
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-xs">
            {!showRiskyAdvances && (
              <Button
                variant="approve"
                onClick={this.markAllPendingPaid}>
                Mark all pending paid
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() =>
                window.open(`${environment.baseUrl}/advancerequests/peach`)
              }>
              Download peach
            </Button>
            {showRiskyAdvances ? (
              <Button variant="primary" onClick={() => this.clearRiskyFilters()}>
                Show Pending Advances
              </Button>
            ) : (
              <Button variant="primary" onClick={() => this.showRiskyAdvances()}>
                Advance Risk Queue
              </Button>
            )}
          </div>
        </div>

        {showRiskyAdvances ? (
          <Card className="shadow border-0">
            <div className="table-responsive">
              <RiskyAdvancesTable
                changeAdvanceStatus={this.changeAdvanceStatus}
              />
            </div>
          </Card>
        ) : (
          <>
            <Card className="shadow border-0">
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={advance_requests}
                  loading={loading}
                  emptyState={emptyState}
                  skeletonRows={8}
                />
              </div>
            </Card>

            {pages > 1 && (
              <div className="mt-md flex justify-center">
                <Pager
                  currentPage={page}
                  totalPages={pages}
                  onPageChange={this.setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

export default AdvanceList;
